import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.showLoadingState();
    // REMOVED: Flying animation call
    // if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  // REMOVED: #animateFlyToCart() method - entire method deleted

  /**
   * Shows loading state immediately when button is clicked.
   */
  showLoadingState() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    // Remove any existing states
    addToCartButton.classList.remove('atc-added');
    
    // Add loading state
    addToCartButton.classList.add('atc-loading');
  }

  /**
   * Shows success state when item is successfully added to cart.
   */
  showSuccessState() {
    const { addToCartButton } = this.refs;

    // Remove loading state and add success state
    addToCartButton.classList.remove('atc-loading');
    addToCartButton.classList.add('atc-added');

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Resets button state (removes loading/success states).
   */
  resetButtonState() {
    const { addToCartButton } = this.refs;
    
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    
    addToCartButton.classList.remove('atc-loading', 'atc-added');
  }


}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The variant ID.
 * @property {HTMLInputElement | undefined} sellingPlan - The selling plan ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {Object | null} */
  #variantSellingPlanMap = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // 🔧 ENHANCED: Listen for subscription selector updates
    document.addEventListener('subscription:selling-plan-update', this.#onSubscriptionUpdate, { signal });

    // Initialize selling plan mapping
    this.#initializeSellingPlanMapping();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Initialize the variant to selling plan mapping from the subscription selector
   */
  #initializeSellingPlanMapping() {
    const subscriptionSelector = document.querySelector('.subscription-selector');
    if (!subscriptionSelector) return;

    // Try both package and variant maps
    const mapScript = subscriptionSelector.querySelector('[data-variant-selling-plan-map]') ||
                      subscriptionSelector.querySelector('[data-package-selling-plan-map]');
    
    if (mapScript && mapScript.textContent) {
      try {
        this.#variantSellingPlanMap = JSON.parse(mapScript.textContent);
        console.log('📋 Product form loaded selling plan map:', this.#variantSellingPlanMap);
      } catch (error) {
        console.warn('Failed to parse selling plan map:', error);
      }
    }
  }

  /**
   * 🔧 ENHANCED: Handle subscription selector updates
   * @param {CustomEvent} event - The subscription update event
   */
  #onSubscriptionUpdate = (event) => {
    const { sellingPlanId, isSubscription, quantity } = event.detail;
    
    console.log('🔄 Product form received subscription update:', event.detail);
    
    // Update selling plan input
    const sellingPlanInput = this.refs.sellingPlan || this.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput) {
      sellingPlanInput.value = sellingPlanId || '';
      console.log('📝 Product form updated selling plan input:', sellingPlanInput.value);
    }
    
    // Update quantity input if needed
    const quantityInput = this.querySelector('input[name="quantity"]');
    if (quantityInput && quantity) {
      quantityInput.value = quantity;
      console.log('📦 Product form updated quantity input:', quantityInput.value);
    }
  };

  /**
   * Get the selling plan ID for a given variant ID
   * @param {string} variantId - The variant ID
   * @returns {string} The selling plan ID or empty string
   */
  #getSellingPlanForVariant(variantId) {
    if (!this.#variantSellingPlanMap || !variantId) return '';

    const variantData = this.#variantSellingPlanMap[variantId];
    return variantData?.planId || '';
  }

  /**
   * Check if subscription mode is currently active
   * @returns {boolean} True if subscription mode is active
   */
  #isSubscriptionModeActive() {
    const subscriptionSelector = document.querySelector('.subscription-selector');
    if (!subscriptionSelector) return false;

    const toggle = subscriptionSelector.querySelector('[data-subscription-toggle]');
    return toggle?.classList.contains('active') || false;
  }

  /**
   * Update the selling plan input based on current selection
   * @param {string} variantId - The current variant ID
   */
  #updateSellingPlanInput(variantId) {
    const sellingPlanInput = this.refs.sellingPlan || this.querySelector('input[name="selling_plan"]');
    if (!sellingPlanInput) return;

    if (this.#isSubscriptionModeActive() && variantId) {
      const sellingPlanId = this.#getSellingPlanForVariant(variantId);
      sellingPlanInput.value = sellingPlanId;
    } else {
      sellingPlanInput.value = '';
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // 🔧 ENHANCED: Log form data for debugging
    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);
    console.log('🛒 Product form submitting with data:');
    console.log('  - Variant ID:', formData.get('id'));
    console.log('  - Quantity:', formData.get('quantity'));
    console.log('  - Selling Plan:', formData.get('selling_plan'));
    
    // Send the add to cart information to the cart
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.root + 'cart/add.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error('❌ Cart add error:', response.message);
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          // Reset button state on error
          this.refs.addToCartButtonContainer?.resetButtonState();

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          console.log('✅ Cart add successful');
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Show success state just before cart drawer opens
          this.refs.addToCartButtonContainer?.showSuccessState();

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error('❌ Fetch error:', error);
        // Reset button state on network/fetch error
        this.refs.addToCartButtonContainer?.resetButtonState();
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButton.setAttribute('disabled', 'true');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.refs.addToCartButton.removeAttribute('disabled');
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    const newVariantId = event.detail.resource.id ?? '';
    variantId.value = newVariantId;

    // ENHANCED: Update the selling plan input based on current subscription selection
    this.#updateSellingPlanInput(newVariantId);

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // ENHANCED: Dispatch custom event for subscription selector to listen to
    const subscriptionUpdateEvent = new CustomEvent('subscription:variant-update', {
      detail: {
        variantId: newVariantId,
        variant: event.detail.resource
      }
    });
    document.dispatchEvent(subscriptionUpdateEvent);
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

// REMOVED: Entire FlyToCart class - completely deleted
// REMOVED: bezierPoint function - completely deleted